import _pick from 'lodash/pick'
import { usePlausible } from 'next-plausible'
import { Router, useRouter } from 'next/router'
import type { ParsedUrlQuery } from 'node:querystring'
import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import {
  DOMAIN,
  GETSAFE_TYPEFORM_DOMAIN,
  SEGMENT_IO_ID,
  WEB_BUYING_FLOW_DOMAIN,
  isDev,
} from '@/config/env'
import * as RUMService from '@/services/RUM'
import { reportError } from '@/services/errorReporting'
import { getFacebookParams } from '@/utils/cookies'
import { getLocaleParts } from '@/utils/locale'
import { isScriptAllowed } from '@helpers/cookies/segmentUtils'
import type { Destination, UserConsent } from '@helpers/cookies/types'
import { AnalyticsBrowser } from '@segment/analytics-next'

const writeKey = SEGMENT_IO_ID
const cdnURL = 'https://seg-cdn.hellogetsafe.com'

const AnalyticsContext = createContext<
  | {
      track: (event: string, properties?: Record<string, any>) => void
      deviceCategory: 'mobile' | 'desktop'
      conditionallyLoadSegmentAnalytics: (consent: UserConsent) => Promise<void>
      appendQueryParametersToAnchorTags: () => void
    }
  | undefined
>(undefined)
//
// Fetch all tools connected/loaded via segment
const fetchDestinations = async (): Promise<Destination[]> => {
  const res = await fetch(`${cdnURL}/v1/projects/${writeKey}/integrations`)

  if (!res.ok) {
    throw new Error(
      `Failed to fetch integrations for write key ${writeKey}: HTTP ${res.status} ${res.statusText}`
    )
  }
  return res.json()
}

const getMarketingParamsFromQuery = (query: ParsedUrlQuery) => {
  const marketingFields = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_id', 'gbraid', 'gclid']
  return _pick(query, marketingFields)
}

export const AnalyticsProvider = ({ children }: { children: ReactNode }) => {
  const { locale, query } = useRouter()
  const plausible = usePlausible()
  const [, market] = getLocaleParts(locale)
  const [segmentLoaded, setSegmentLoaded] = useState(false)
  const [deviceCategory, setDeviceCategory] = useState<'mobile' | 'desktop'>('desktop')
  const segmentAnalytics = useMemo(() => new AnalyticsBrowser(), [])

  const appendQueryParametersToAnchorTags = useCallback(() => {
    document.querySelectorAll('a').forEach((a) => {
      if (
        a.href.includes(DOMAIN) ||
        a.href.includes(WEB_BUYING_FLOW_DOMAIN) ||
        a.href.includes(GETSAFE_TYPEFORM_DOMAIN)
      ) {
        const searchParams = new URL(a.baseURI).searchParams
        const newHref = new URL(a.href)

        // eslint-disable-next-line no-restricted-syntax
        for (const [key, value] of searchParams.entries()) {
          if (!newHref.searchParams.has(key)) {
            newHref.searchParams.append(key, value)
          }
        }

        // eslint-disable-next-line no-param-reassign
        a.href = newHref.toString()
      }
    })
  }, [])

  const conditionallyLoadSegmentAnalytics = useCallback(
    async (consent: UserConsent) => {
      if (!consent || writeKey === 'off' || typeof window === 'undefined') {
        appendQueryParametersToAnchorTags()
        return
      }

      if (segmentLoaded) {
        window.location.reload()
      }
      const integrations: Record<string, boolean> = {
        All: false,
        'Segment.io': 'segment' in consent ? consent.segment : false,
      }
      let isAnythingEnabled = false
      const destinations = await fetchDestinations()
      for (const destination of destinations) {
        const isEnabled = isScriptAllowed(destination.creationName, consent)
        if (isEnabled) {
          isAnythingEnabled = true
        }
        integrations[destination.creationName] = isEnabled
      }
      if (isAnythingEnabled) {
        // This is better than user agent tracking since it is more reliable for modern browsers
        // https://developer.mozilla.org/en-US/docs/Web/API/Navigator/userAgent
        setDeviceCategory(window.matchMedia('(max-width: 767px)').matches ? 'mobile' : 'desktop')
        segmentAnalytics.load({ writeKey, cdnURL }, { integrations })
        setSegmentLoaded(true)
        segmentAnalytics
          .page(undefined, undefined, {
            deviceCategory,
          })
          .catch(reportError)
      }
      appendQueryParametersToAnchorTags()
    },
    [appendQueryParametersToAnchorTags, deviceCategory, segmentAnalytics, segmentLoaded]
  )

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      if (url && segmentLoaded) {
        const marketingParams = getMarketingParamsFromQuery(query)
        segmentAnalytics
          .page(undefined, undefined, {
            deviceCategory,
            ...marketingParams,
          })
          .catch(reportError)
      }
    }

    Router.events.on('routeChangeComplete', handleRouteChange)

    return () => {
      Router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [query, deviceCategory, segmentAnalytics, segmentLoaded])

  const track = (event: string, properties?: Record<string, any>) => {
    if (typeof window === 'undefined') {
      return
    }

    const marketingParams = getMarketingParamsFromQuery(query)

    const fbParams = getFacebookParams()

    const info = { ...marketingParams, ...properties, ...fbParams, market, deviceCategory }

    plausible(event, { props: info })

    RUMService.addAction(event, info)

    if (segmentLoaded) {
      segmentAnalytics.track(event, info).catch(reportError)
      return
    }
    if (isDev()) {
      // eslint-disable-next-line no-console
      console.log(`Track: ${event}, ${JSON.stringify(info)}`)
    }
  }

  return (
    <AnalyticsContext.Provider
      value={{
        track,
        deviceCategory,
        conditionallyLoadSegmentAnalytics,
        appendQueryParametersToAnchorTags,
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  )
}

// Create an analytics hook that we can use with other components.
export const useAnalytics = () => {
  const result = useContext(AnalyticsContext)
  if (!result) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider')
  }
  return result
}
